import { useParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useCollection, useCollectionVideos } from '@/hooks/useCollection';
import { VideoCard } from '@/components/VideoCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getCollectionTitle, getCollectionDescription } from '@/hooks/useCollections';

export function CollectionPage() {
  const { npub, dtag } = useParams<{ npub: string; dtag: string }>();
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  // Decode the npub to get the pubkey
  let pubkey: string | null = null;
  if (npub) {
    try {
      const decoded = nip19.decode(npub);
      if (decoded.type === 'npub') {
        pubkey = decoded.data;
      }
    } catch {
      // Invalid npub
    }
  }

  const { data: collection, isLoading: collectionLoading } = useCollection(
    pubkey,
    dtag
  );

  const { data: videos, isLoading: videosLoading } = useCollectionVideos(collection);

  if (collectionLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-4 w-96 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="aspect-video w-full rounded-lg" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-muted-foreground">Collection not found.</p>
      </div>
    );
  }

  const title = getCollectionTitle(collection);
  const description = getCollectionDescription(collection);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Neon border accent */}
      <div className="fixed top-0 left-0 w-1 h-full bg-gradient-to-b from-pink-500 via-cyan-400 to-transparent opacity-50" />

      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{title}</h1>
          {description && (
            <p className="text-muted-foreground">{description}</p>
          )}
        </div>

        {videosLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-video w-full rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : videos && videos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videos.map((event) => (
              <VideoCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="max-w-sm mx-auto space-y-4">
              <div className="text-6xl mb-4">📽️</div>
              <h2 className="text-2xl font-semibold">Empty collection</h2>
              <p className="text-muted-foreground">
                This collection has no videos yet. Add some bookmarks to see them here.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}